import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { DateTime as DT } from "luxon"
import time from "../time"

type PickerMode = "date" | "time" | "datetime"

/**
 * Stimulus controller for flatpickr date/time pickers.
 *
 * Usage:
 *   Date picker:
 *     <input data-controller="flatpickr"
 *            data-flatpickr-mode-value="date"
 *            data-flatpickr-date-format-value="m/d/Y" />
 *
 *   Time picker:
 *     <input data-controller="flatpickr"
 *            data-flatpickr-mode-value="time" />
 *
 *   Date picker with timezone support:
 *     <input data-controller="flatpickr"
 *            data-flatpickr-mode-value="date"
 *            data-flatpickr-timezone-value="America/Los_Angeles"
 *            data-flatpickr-min-date-value="today" />
 *
 *   Using as an outlet from another controller:
 *     // In parent controller:
 *     static outlets = ["flatpickr"]
 *     // Access: this.flatpickrOutlets[0].selectedDates
 */
export default class FlatpickrController extends Controller<HTMLInputElement> {
  declare modeValue: PickerMode
  declare dateFormatValue: string
  declare timezoneValue: string
  declare minDateValue: string
  declare maxDateValue: string
  declare defaultDateValue: string

  declare hasModeValue: boolean
  declare hasDateFormatValue: boolean
  declare hasTimezoneValue: boolean
  declare hasMinDateValue: boolean
  declare hasMaxDateValue: boolean
  declare hasDefaultDateValue: boolean

  static values = {
    mode: { type: String, default: "date" },
    dateFormat: { type: String, default: "" },
    timezone: { type: String, default: "" },
    minDate: { type: String, default: "" },
    maxDate: { type: String, default: "" },
    defaultDate: { type: String, default: "" },
  }

  private picker: flatpickr.Instance | null = null

  connect() {
    this.initializePicker()
  }

  disconnect() {
    this.picker?.destroy()
    this.picker = null
  }

  private initializePicker() {
    const options = this.buildOptions()
    this.picker = flatpickr(this.element, options)
  }

  private buildOptions(): flatpickr.Options.Options {
    const mode = this.modeValue as PickerMode
    const options: flatpickr.Options.Options = {
      // Dispatch change event when date is selected, allowing parent controllers to respond
      onChange: () => {
        this.element.dispatchEvent(new Event("change", { bubbles: true }))
      },
    }

    if (mode === "date") {
      options.enableTime = false
      options.dateFormat = this.hasDateFormatValue && this.dateFormatValue
        ? this.dateFormatValue
        : time.FLATPICKR_DATE_FORMAT

      if (this.hasMinDateValue && this.minDateValue) {
        // Handle "today" specially when timezone is set - use the user's timezone "today"
        // Otherwise flatpickr uses browser local timezone which can cause mismatches
        if (this.minDateValue === "today" && this.hasTimezoneValue && this.timezoneValue) {
          const userToday = DT.now().setZone(this.timezoneValue).startOf("day")
          options.minDate = new Date(userToday.year, userToday.month - 1, userToday.day)
        } else {
          options.minDate = this.minDateValue
        }
      }

      if (this.hasMaxDateValue && this.maxDateValue) {
        options.maxDate = this.maxDateValue
      }

      // Handle default date with timezone support
      if (this.element.value && this.hasTimezoneValue && this.timezoneValue) {
        // Use LUXON_DATE format since Ruby's to_fs(:date) outputs "MMM dd, yyyy" format
        const userDate = time.parseDT(this.element.value, "LUXON_DATE", this.timezoneValue)
        if (userDate.isValid) {
          options.defaultDate = new Date(userDate.year, userDate.month - 1, userDate.day)
        } else {
          console.warn("Invalid date value:", this.element.value, userDate.invalidReason)
        }
      } else if (this.hasDefaultDateValue && this.defaultDateValue) {
        options.defaultDate = this.defaultDateValue
      }
    } else if (mode === "time") {
      options.enableTime = true
      options.noCalendar = true
      options.dateFormat = this.hasDateFormatValue && this.dateFormatValue
        ? this.dateFormatValue
        : time.FLATPICKR_TIME_FORMAT
      options.minuteIncrement = time.MINUTE_INCREMENT

      // Handle default time with timezone support
      // Note: We need to set defaultDate (not just defaultHour/defaultMinute) to populate selectedDates
      if (this.element.value && this.hasTimezoneValue && this.timezoneValue) {
        // Use LUXON_TIME format since Ruby's to_fs(:time) outputs "h:mm a" format
        const userTime = time.parseDT(this.element.value, "LUXON_TIME", this.timezoneValue)
        if (userTime.isValid) {
          // Create a date with the parsed time to populate selectedDates
          const now = new Date()
          options.defaultDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), userTime.hour, userTime.minute)
        } else {
          console.warn("Invalid time value:", this.element.value, userTime.invalidReason)
        }
      }
    } else if (mode === "datetime") {
      options.enableTime = true
      options.dateFormat = this.hasDateFormatValue && this.dateFormatValue
        ? this.dateFormatValue
        : `${time.FLATPICKR_DATE_FORMAT} ${time.FLATPICKR_TIME_FORMAT}`

      if (this.hasMinDateValue && this.minDateValue) {
        options.minDate = this.minDateValue
      }

      if (this.hasMaxDateValue && this.maxDateValue) {
        options.maxDate = this.maxDateValue
      }
    }

    return options
  }

  /**
   * Get the selected dates from the picker.
   */
  get selectedDates(): Date[] {
    return this.picker?.selectedDates || []
  }

  /**
   * Get the underlying flatpickr instance.
   */
  get instance(): flatpickr.Instance | null {
    return this.picker
  }

  /**
   * Get the selected value as an ISO string in the user's timezone.
   * Useful for form submission when timezone conversion is needed.
   */
  getTimezoneValue(): string {
    if (!this.picker?.selectedDates?.length || !this.hasTimezoneValue || !this.timezoneValue) {
      return ""
    }

    const dt = DT.fromJSDate(this.picker.selectedDates[0])
    const userDateTime = DT.fromObject(
      {
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
      },
      {
        zone: this.timezoneValue,
      }
    )

    return userDateTime.toISO() || ""
  }
}
